import { useEffect, useCallback } from 'react';
import { getModifierKey, isMac } from '@/utils/keyboard';

export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  description: string;
  action: () => void;
  preventDefault?: boolean;
}

export const useKeyboardShortcuts = (shortcuts: KeyboardShortcut[], enabled = true) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Check if user is typing in an input field or code editor
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]') ||
        target.closest('.cm-editor') ||
        target.classList.contains('cm-editor');

      for (const shortcut of shortcuts) {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatch = shortcut.ctrl ? event.ctrlKey || event.metaKey : !event.ctrlKey && !event.metaKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;
        const metaMatch = shortcut.meta ? event.metaKey : !event.metaKey;

        if (keyMatch && ctrlMatch && shiftMatch && altMatch && metaMatch) {
          // Skip shortcuts when typing in input fields (except Escape)
          if (isInputField && shortcut.key.toLowerCase() !== 'escape') {
            continue;
          }

          if (shortcut.preventDefault !== false) {
            event.preventDefault();
          }
          shortcut.action();
          break;
        }
      }
    },
    [shortcuts, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
};

// Common keyboard shortcuts
export const COMMON_SHORTCUTS = {
  SAVE: { key: 's', ctrl: true, description: 'Save' },
  UNDO: { key: 'z', ctrl: true, description: 'Undo' },
  REDO: { key: 'z', ctrl: true, shift: true, description: 'Redo' },
  COPY: { key: 'c', ctrl: true, description: 'Copy' },
  PASTE: { key: 'v', ctrl: true, description: 'Paste' },
  CUT: { key: 'x', ctrl: true, description: 'Cut' },
  DELETE: { key: 'Delete', description: 'Delete' },
  ESCAPE: { key: 'Escape', description: 'Close/Cancel' },
  ENTER: { key: 'Enter', description: 'Confirm' },
  SEARCH: { key: 'f', ctrl: true, description: 'Search' },
  NEW: { key: 'n', ctrl: true, description: 'New' },
  OPEN: { key: 'o', ctrl: true, description: 'Open' },
  CLOSE: { key: 'w', ctrl: true, description: 'Close' },
  REFRESH: { key: 'r', ctrl: true, description: 'Refresh' },
  ZOOM_IN: { key: '+', ctrl: true, description: 'Zoom In' },
  ZOOM_OUT: { key: '-', ctrl: true, description: 'Zoom Out' },
  ZOOM_RESET: { key: '0', ctrl: true, description: 'Reset Zoom' },
  SELECT_ALL: { key: 'a', ctrl: true, description: 'Select All' },
  TOGGLE_PANEL: { key: '`', ctrl: true, description: 'Toggle Panel' },
};

// Format shortcut for display (OS-aware)
export const formatShortcut = (shortcut: Partial<KeyboardShortcut>): string => {
  const parts: string[] = [];
  const modifierKey = getModifierKey();
  const isMacOS = isMac();

  // Use OS-appropriate modifier key
  if (shortcut.ctrl) parts.push(modifierKey);
  if (shortcut.shift) parts.push(isMacOS ? '⇧' : 'Shift');
  if (shortcut.alt) parts.push(isMacOS ? '⌥' : 'Alt');
  if (shortcut.meta) parts.push(isMacOS ? '⌘' : 'Cmd');

  if (shortcut.key) {
    let key = shortcut.key;
    
    // Format special keys
    if (key.toLowerCase() === 'delete' || key.toLowerCase() === 'backspace') {
      key = isMacOS ? '⌫' : 'Del';
    } else if (key.toLowerCase() === 'enter' || key.toLowerCase() === 'return') {
      key = isMacOS ? '↵' : 'Enter';
    } else if (key.toLowerCase() === 'escape' || key.toLowerCase() === 'esc') {
      key = 'Esc';
    } else if (key.length === 1) {
      key = key.toUpperCase();
    }
    
    parts.push(key);
  }

  return parts.join(isMacOS ? '' : ' + ');
};
